import { queryOptions, useQuery } from '@tanstack/react-query';

import { QueryOptions } from '@cast/shared-types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const apiFn = apiClient.dbo.getCacheQueryTemplate;

export type UseCacheQueryTemplateQueryArgs<SELECT> = QueryOptions<
  string | undefined,
  SELECT
> & {
  cacheGroupId?: string;
  cacheId?: string;
  templateHash?: string;
};

const DBO_CACHE_QUERY_TEMPLATE_QUERY_KEY = 'dbo-cache-query-template';

const getCacheQueryTemplateQueryKey = ({
  cacheGroupId,
  cacheId,
  templateHash,
}: { cacheGroupId?: string; cacheId?: string; templateHash?: string } = {}) =>
  [
    DBO_CACHE_QUERY_TEMPLATE_QUERY_KEY,
    cacheGroupId,
    cacheId,
    templateHash,
  ] as const;

const getCacheQueryTemplateQueryOptions = ({
  cacheGroupId,
  cacheId,
  templateHash,
  enabled,
}: {
  cacheGroupId?: string;
  cacheId?: string;
  templateHash?: string;
  enabled?: boolean;
}) =>
  queryOptions({
    queryKey: getCacheQueryTemplateQueryKey({
      cacheGroupId,
      cacheId,
      templateHash,
    }),
    queryFn: async () => {
      const { data } = await apiFn({
        groupId: cacheGroupId!,
        cacheId: cacheId!,
        templateHash: templateHash!,
      });
      return data.template;
    },
    enabled: makeIsEnabled(!!cacheGroupId, !!cacheId, !!templateHash, enabled),
  });

export const useCacheQueryTemplateQuery = <SELECT = string>({
  cacheGroupId,
  cacheId,
  templateHash,
  enabled,
  refetchInterval,
}: UseCacheQueryTemplateQueryArgs<SELECT>) => {
  return useQuery({
    ...getCacheQueryTemplateQueryOptions({
      cacheGroupId,
      cacheId,
      templateHash,
      enabled,
    }),
    refetchInterval,
  });
};
